/**
 * Typography tokens.
 *
 * System-native typefaces are used across headings, body, and UI text, with
 * a type scale tuned for dense, data-heavy security dashboards.
 *
 * @see https://mui.com/material-ui/react-typography/
 */

export const fontFamily = {
  /** Native UI sans-serif stack for the current operating system. */
  primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  /** Alias of `primary` for theme and component usage. */
  ui: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  /** Native monospace stack for the current operating system. */
  mono:
    'Menlo, Monaco, "SF Mono", "Liberation Mono", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", Consolas, "Lucida Console", "Courier New", monospace',
} as const;

export const fontWeight = {
  /** Thin — 100 */
  thin: 100,
  /** Extra Light — 200 */
  extraLight: 200,
  /** Light — 300 */
  light: 300,
  /** Regular — 400 */
  regular: 400,
  /** Medium — 500 */
  medium: 500,
  /** Semi Bold — 600 */
  semibold: 600,
  /** Bold — 700 */
  bold: 700,
  /** Extra Bold — 800 */
  extraBold: 800,
  /** Black — 900 */
  black: 900,
} as const;

/** Common upright and italic font styles. */
export const fontStyle = {
  normal: 'normal',
  italic: 'italic',
} as const;

/**
 * Common OpenType features. Browsers ignore features unsupported by the
 * selected system font.
 */
export const fontFeatureSettings = {
  /** Ligatures + contextual alternates (recommended default for UI text). */
  default: "'liga' 1, 'calt' 1",
  /** Fixed-width figures for aligned numeric columns. */
  tabularNums: "'tnum' 1",
  /** Slashed zero to disambiguate 0 from o. */
  slashedZero: "'zero' 1",
  /** Tabular nums + slashed zero — common for security dashboards. */
  dataDisplay: "'tnum' 1, 'zero' 1, 'liga' 1, 'calt' 1",
} as const;

/** Font sizes in pixels. */
export const fontSize = {
  xs: 11,
  sm: 12,
  md: 14,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 48,
  '5xl': 64,
} as const;

/**
 * Size for a glyph sitting inside a compact label -- a chip's leading icon, its
 * delete X, a picker's chevron.
 *
 * Expressed as an `em` ratio rather than a pixel value so one rule covers every
 * size a chip comes in: it resolves to `lg` (16px) against the 12px medium
 * label, ~14.7px against the 11px small one, ~18.7px against the 14px large
 * label, and ~21.3px against the 16px extraLarge label, and it follows
 * `Chip purpose="entity"` when that sizes itself to the surrounding prose.
 *
 * The ratio is derived rather than written out because the whole point is that
 * the glyph tracks the label. MUI's own chip leaves the medium icon unsized, so
 * it fell back to the 24px `SvgIcon` default -- twice the label it sat beside.
 */
export const compactGlyphScale = `${fontSize.lg / fontSize.sm}em`;

export const lineHeight = {
  tight: 1.2,
  normal: 1.4,
  relaxed: 1.6,
} as const;

export const letterSpacing = {
  tight: '-0.01em',
  normal: '0',
  wide: '0.02em',
} as const;

export const typographyTokens = {
  fontFamily,
  fontWeight,
  fontStyle,
  fontFeatureSettings,
  fontSize,
  lineHeight,
  letterSpacing,
} as const;

export type FontSizeToken = keyof typeof fontSize;

/**
 * Which MUI Typography variant to reach for. The counterpart to
 * `spacingUsage` / `backgroundUsage`: it keeps the "which variant do I use"
 * answer next to the type tokens so the agent kit and Storybook stay in sync.
 *
 * Sizes and weights live in `src/theme/typography.ts`. This table is the role
 * mapping — never pick a variant for its pixel size alone.
 */
export const typographyVariantUsage = {
  h1: { useFor: 'Rare. Marketing or empty-state heroes — not product chrome.' },
  h2: { useFor: 'Major page sections that sit above a page title.' },
  h3: { useFor: 'KPI figures and large stats (StatTile value).' },
  h4: { useFor: 'Page titles in templates (TablePageLayout, DetailsPageLayout).' },
  h5: { useFor: 'Investigation or subsection titles.' },
  h6: { useFor: 'Drawer headings and empty-state titles.' },
  subtitle1: { useFor: 'Card titles, form section titles, accordion summaries, dialog titles.' },
  subtitle2: { useFor: 'Compact headings: filter panels, table row titles, widget headers.' },
  body1: { useFor: 'Default reading text (14px): descriptions, dialog body, card copy, breadcrumbs.' },
  body2: { useFor: 'Dense annotations only (12px): deltas, compact secondary lines. Not default copy.' },
  caption: { useFor: 'Metadata, timestamps, helper counts, form labels, tooltips.' },
  overline: { useFor: 'Column headers, uppercase tags, category labels.' },
  button: { useFor: 'Reserved for Button. Do not use for prose.' },
} as const;

export type TypographyVariantName = keyof typeof typographyVariantUsage;
